import {useState, useEffect, useCallback} from 'react';
import {useProducts} from './useProducts';
import {useFormState} from '../../../Presentation/utils/formUtils';
import {ProductFormValidation, ProductFormInitialData} from '../models/ProductFormTypes';

/**
 * Custom hook for Add Product Form logic
 * Follows clean architecture by separating business logic from UI
 */
export const useAddProductForm = (initialData?: ProductFormInitialData) => {
  const {brands, brandsLoading, tags, tagsLoading, productTypes, typesLoading} = useProducts({autoFetch: true});

  // Form state management
  const [formState, formActions] = useFormState({
    name: '',
    type: '',
    brandId: '',
    selectedTag: '',
    price: '',
    barcode: '',
    hasSerial: false,
    quantity: '',
    images: [] as File[],
    serials: [] as string[]
  });

  // Additional state for complex logic
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  // Initialize form with initial data
  useEffect(() => {
    if (!initialData) {return;}

    if (initialData.name) {formActions.setField('name', initialData.name);}
    if (initialData.typeId != null) {formActions.setField('type', String(initialData.typeId));}
    if (initialData.brandId != null) {formActions.setField('brandId', String(initialData.brandId));}
    if (initialData.price != null) {formActions.setField('price', String(initialData.price));}
    if (initialData.barcode != null) {formActions.setField('barcode', initialData.barcode);}
    if (initialData.hasSerial != null) {formActions.setField('hasSerial', initialData.hasSerial);}
    if (initialData.tags && initialData.tags.length > 0) {
      formActions.setField('selectedTag', String(initialData.tags[0]));
    }
  }, [initialData]);

  // Reset serial fields based on product type
  useEffect(() => {
    if (!formState.data.hasSerial) {
      formActions.setField('serials', []);
    } else {
      formActions.setField('quantity', '');
      if (formState.data.serials.length === 0) {
        formActions.setField('serials', ['']);
      }
    }
  }, [formState.data.hasSerial]);

  // Auto-calculate quantity for serial products
  useEffect(() => {
    if (formState.data.hasSerial && Array.isArray(formState.data.serials)) {
      const validSerials = formState.data.serials.filter(s => s && typeof s === 'string' && s.trim());
      formActions.setField('quantity', String(validSerials.length));
    }
  }, [formState.data.hasSerial, formState.data.serials]);

  // Generate image previews for uploaded files
  useEffect(() => {
    imagePreviews.forEach(url => URL.revokeObjectURL(url));
    const images = Array.isArray(formState.data.images) ? formState.data.images : [];
    setImagePreviews(images.map(img => URL.createObjectURL(img)));
  }, [formState.data.images]);

  // Initialize form state
  useEffect(() => {
    formActions.clearErrors();
  }, []);

  /**
   * Validates form data with context-aware rules
   * @param isEditMode - Whether this is edit mode (images optional)
   * @param allowStockChanges - Whether stock validation should be applied
   * @returns Validation result with errors
   */
  const validateForm = useCallback((isEditMode: boolean = false, allowStockChanges: boolean = true): ProductFormValidation => {
    const errors: Record<string, string> = {};
    const data = formState.data;

    // Required field validations
    if (!data.name || !data.name.trim()) {errors.name = 'اسم المنتج مطلوب';}
    if (!data.type || isNaN(Number(data.type))) {errors.type = 'نوع المنتج مطلوب ويجب أن يكون رقمًا';}
    if (!data.brandId || isNaN(Number(data.brandId))) {errors.brandId = 'البراند مطلوب ويجب أن يكون رقمًا';}
    if (!data.price || isNaN(Number(data.price))) {errors.price = 'السعر مطلوب ويجب أن يكون رقمًا';}

    // Image validation - only required for new products, not edits
    const images = Array.isArray(data.images) ? data.images : [];
    if (!isEditMode && !images.length) {
      errors.images = 'الصورة مطلوبة';
    }

    // Stock management validations - only when stock changes are allowed
    if (allowStockChanges) {
      if (!data.hasSerial) {
        if (data.quantity && (isNaN(Number(data.quantity)) || Number(data.quantity) < 0)) {
          errors.quantity = 'الكمية يجب أن تكون رقمًا صحيحًا 0 أو أكثر';
        }
      } else {
        // For serial products, quantity should match the number of valid serials
        const validSerials = Array.isArray(data.serials) ? data.serials.filter(s => s && typeof s === 'string' && s.trim()) : [];
        const expectedQuantity = validSerials.length;

        if (data.quantity && Number(data.quantity) !== expectedQuantity) {
          errors.quantity = `الكمية يجب أن تساوي عدد السيريالات (${expectedQuantity})`;
        }

        // Validate serials if any are entered
        if (data.serials.length > 0) {
          const validSerials = data.serials.filter(s => s && typeof s === 'string' && s.trim());
          if (validSerials.length !== data.serials.length) {
            errors.serials = 'يجب أن تكون جميع السيريالات صحيحة ولا يمكن تركها فارغة';
          }
        }
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }, [formState.data, initialData]);

  /**
   * Handles form submission with validation
   * @param onSubmit - Callback function to handle form data
   * @param isEditMode - Whether this is edit mode
   * @param allowStockChanges - Whether stock changes are allowed
   * @returns Success boolean
   */
  const handleSubmit = useCallback((onSubmit: (data: FormData) => void, isEditMode: boolean = false, allowStockChanges: boolean = true) => {
    const validation = validateForm(isEditMode, allowStockChanges);

    if (!validation.isValid) {
      formActions.setErrors(validation.errors);
      return false;
    }

    formActions.clearErrors();

    const data = formState.data;
    const formData = new FormData();

    // Basic product data
    formData.append('name', data.name);
    formData.append('type_id', String(Number(data.type)));
    formData.append('brand_id', String(Number(data.brandId)));
    formData.append('price', String(Number(data.price)));
    if (data.barcode && data.barcode.trim()) {
      formData.append('barcode', data.barcode);
    }
    formData.append('has_serial', data.hasSerial ? 'true' : 'false');

    // Tags
    if (data.selectedTag) {
      formData.append('tags[]', String(data.selectedTag));
    }

    // Images
    const images = Array.isArray(data.images) ? data.images : [];
    images.forEach(img => {
      formData.append('images', img);
    });

    // Stock management
    if (!data.hasSerial && data.quantity) {
      formData.append('quantity', String(Number(data.quantity)));
    }

    if (data.hasSerial && data.serials.length > 0) {
      const validSerials = data.serials.filter(s => s && typeof s === 'string' && s.trim());
      // Send serials as array fields
      validSerials.forEach(serial => {
        formData.append('serials[]', serial);
      });
    }

    onSubmit(formData);
    return true;
  }, [formState.data, validateForm, formActions]);

  // Image management
  const handleImageRemove = useCallback((index: number) => {
    const currentImages = Array.isArray(formState.data.images) ? formState.data.images : [];
    formActions.setField('images', currentImages.filter((_, idx) => idx !== index));
  }, [formState.data.images, formActions]);

  return {
    // Form state
    formState,
    formActions,
    imagePreviews,

    // Data
    brands,
    tags,
    productTypes,

    // Loading states
    brandsLoading,
    tagsLoading,
    typesLoading,

    // Actions
    handleSubmit,
    handleImageRemove,
    validateForm
  };
};
